import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { KeyRound, ShieldAlert, ShieldCheck, UserPlus } from "lucide-react";

const PasswordCard = () => {
    const [form, setForm] = useState({ current: "", new: "", confirm: "" });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.new !== form.confirm) {
            toast.error("Die neuen Passwörter stimmen nicht überein.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-password", { current: form.current, new: form.new });
            toast.success("Passwort geändert — ab sofort gilt das neue Passwort.");
            setForm({ current: "", new: "", confirm: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="rounded border border-[#2E2517] bg-[#1B150E] p-6" data-testid="password-card">
            <div className="mb-4 flex items-center gap-3">
                <KeyRound size={18} className="text-[#C88A45]" />
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-[#EDE6DA]">Passwort ändern</h3>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                    <label htmlFor="pw-current" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Aktuell</label>
                    <Input id="pw-current" data-testid="pw-current" type="password" required value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" />
                </div>
                <div>
                    <label htmlFor="pw-new" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Neu (min. 8 Zeichen)</label>
                    <Input id="pw-new" data-testid="pw-new" type="password" required minLength={8} value={form.new} onChange={(e) => setForm({ ...form, new: e.target.value })} className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" />
                </div>
                <div>
                    <label htmlFor="pw-confirm" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Neu wiederholen</label>
                    <Input id="pw-confirm" data-testid="pw-confirm" type="password" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" />
                </div>
            </div>
            <button
                data-testid="pw-save"
                type="submit"
                disabled={saving}
                className="mt-5 rounded-full bg-[#C88A45] px-6 py-2.5 text-xs font-bold uppercase tracking-wide text-[#120E09] transition-colors duration-200 hover:bg-[#DBA057] disabled:opacity-50"
            >
                {saving ? "Speichern …" : "Passwort ändern"}
            </button>
        </form>
    );
};

const UsersCard = () => {
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    const create = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/admin/users", form);
            toast.success(`Zugang für ${form.email} angelegt.`);
            setForm({ name: "", email: "", password: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (email) => {
        try {
            await api.delete(`/admin/users/${encodeURIComponent(email)}`);
            toast.success(`Zugang ${email} entfernt.`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="mt-8 rounded border border-[#2E2517] bg-[#1B150E] p-6" data-testid="users-card">
            <div className="mb-4 flex items-center gap-3">
                <UserPlus size={18} className="text-[#C88A45]" />
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-[#EDE6DA]">Zweiter Zugang</h3>
            </div>
            <p className="mb-5 text-sm leading-relaxed text-[#9C917D]">
                Ja, das geht — legen Sie hier z. B. für Ihre Freundin einen eigenen Zugang an. Sie kann dann mit
                ihrer eigenen E-Mail und ihrem Passwort denselben Admin-Bereich nutzen (Kalender, Notizen, Postfach).
            </p>
            <form onSubmit={create} className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input data-testid="user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" />
                <Input data-testid="user-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-Mail" className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" />
                <Input data-testid="user-password" required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Passwort (min. 8 Zeichen)" className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" />
                <button data-testid="user-create" type="submit" disabled={saving} className="rounded-full bg-[#C88A45] px-6 py-2.5 text-xs font-bold uppercase tracking-wide text-[#120E09] transition-colors duration-200 hover:bg-[#DBA057] disabled:opacity-50 sm:col-span-3 sm:w-fit">
                    {saving ? "Anlegen …" : "Zugang anlegen"}
                </button>
            </form>
            <div className="mt-6 space-y-2" data-testid="users-list">
                {users.map((u) => (
                    <div key={u.email} className="flex items-center justify-between rounded border border-[#2E2517] px-4 py-2 text-sm">
                        <span className="text-[#EDE6DA]">{u.name} <span className="text-[#9C917D]">({u.email})</span></span>
                        <button data-testid={`user-delete-${u.email}`} onClick={() => remove(u.email)} className="text-xs text-[#9C917D] transition-colors hover:text-red-400">
                            Entfernen
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const SecurityAdmin = () => {
    const [sec, setSec] = useState(null);

    useEffect(() => {
        api.get("/admin/security").then((r) => setSec(r.data)).catch(() => {});
    }, []);

    if (!sec) return <p className="text-sm text-[#9C917D]">Lade Sicherheitsstatus …</p>;

    return (
        <div data-testid="admin-security">
            <div
                data-testid="security-warning"
                className={`mb-8 flex items-start gap-3 rounded border p-5 ${
                    sec.warning ? "border-red-400/50 bg-red-400/10 text-red-300" : "border-[#7A9B5A]/40 bg-[#7A9B5A]/10 text-[#7A9B5A]"
                }`}
            >
                {sec.warning ? <ShieldAlert size={20} className="mt-0.5 shrink-0" /> : <ShieldCheck size={20} className="mt-0.5 shrink-0" />}
                <div>
                    <p className="font-semibold">{sec.warning ? "Ungewöhnliche Aktivität erkannt" : "Alles ruhig"}</p>
                    <p className="mt-1 text-sm opacity-80">
                        {sec.warning
                            ? `${sec.failed_24h} fehlgeschlagene Logins in den letzten 24 Stunden. Nach 5 Fehlversuchen wird ein Konto automatisch für 15 Minuten gesperrt. Prüfen Sie die Liste unten.`
                            : "Keine auffälligen Login-Versuche. Ihr Zugang ist durch Passwort-Hashing und automatische Sperren geschützt."}
                    </p>
                </div>
            </div>

            <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded border border-[#2E2517] bg-[#1B150E] p-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Fehlversuche (24 h)</p>
                    <p className="mt-2 font-display text-4xl font-black text-red-400" data-testid="security-failed-24h">{sec.failed_24h}</p>
                </div>
                <div className="rounded border border-[#2E2517] bg-[#1B150E] p-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Fehlversuche (7 Tage)</p>
                    <p className="mt-2 font-display text-4xl font-black text-[#EDE6DA]">{sec.failed_7d}</p>
                </div>
                <div className="rounded border border-[#2E2517] bg-[#1B150E] p-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Erfolgreiche Logins (24 h)</p>
                    <p className="mt-2 font-display text-4xl font-black text-[#7A9B5A]">{sec.success_24h}</p>
                </div>
            </div>

            <div className="mb-8 rounded border border-[#2E2517] bg-[#1B150E] p-6" data-testid="security-log">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Login-Protokoll</p>
                {sec.recent.length === 0 && <p className="text-sm text-[#9C917D]">Noch keine Einträge.</p>}
                <div className="space-y-2">
                    {sec.recent.map((e) => (
                        <div key={e.id} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                            <span className={e.type === "login_failed" || e.type === "login_locked" ? "text-red-400" : "text-[#7A9B5A]"}>{e.text}</span>
                            <span className="text-xs text-[#9C917D]">{new Date(e.created_at).toLocaleString("de-DE")}</span>
                        </div>
                    ))}
                </div>
            </div>

            <PasswordCard />
            <UsersCard />
        </div>
    );
};
